import { FC, useState } from "react";
import { Clock } from "lucide-react";
import toast from "react-hot-toast";

import { Button } from "shared/ui/shadcn/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "shared/ui/shadcn/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "shared/ui/shadcn/select";

import { DailyTodoSettingsI } from "@/api/routes/daily-todos";

import { useSaveDailyTime } from "../data/queries";
import { describeDailyTodosFailure } from "../lib/data";
import {
  DAILY_TIME_OPTIONS,
  formatDailyTime,
  getBrowserTimeZone,
  getDailyTimeOptions,
  getTimeZoneLabel,
} from "../lib/schedule";

/** The daily time picker on its own: a select of 7:00 AM to 9:00 PM. */
export const DailyTimeSelect: FC<{
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  timeZoneLabel?: string;
  options?: Array<{ value: string; label: string }>;
}> = ({
  value,
  onChange,
  disabled,
  timeZoneLabel,
  options = DAILY_TIME_OPTIONS,
}) => (
  <Select value={value} onValueChange={onChange} disabled={disabled}>
    <SelectTrigger aria-label="Daily reminder time" className="w-full">
      <SelectValue />
    </SelectTrigger>
    <SelectContent className="z-[200] max-h-[280px]">
      {options.map((option) => (
        <SelectItem key={option.value} value={option.value}>
          {option.label}
          {timeZoneLabel ? ` ${timeZoneLabel}` : ""}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

/**
 * "Daily reminder 9:00 AM CT" at the right of the page's title row; opens a
 * picker (CC's clock icon). Saved in the browser's time zone.
 */
export const DailyTimeControl: FC<{
  settings: DailyTodoSettingsI;
  isReadOnly?: boolean;
}> = ({ settings, isReadOnly }) => {
  const [isOpen, setIsOpen] = useState(false);
  const save = useSaveDailyTime();
  const zoneLabel = getTimeZoneLabel(settings.time_zone);

  const onChange = (dailyTime: string) => {
    save.mutate(
      { daily_time: dailyTime, time_zone: getBrowserTimeZone() },
      {
        onSuccess: () => {
          toast.success(`Daily reminder set to ${formatDailyTime(dailyTime)}`);
          setIsOpen(false);
        },
        onError: (error) =>
          toast.error(
            describeDailyTodosFailure(error, "Failed to save the daily time")
          ),
      }
    );
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-black/70 hover:text-black"
          disabled={isReadOnly}
        >
          <Clock aria-hidden />
          <span className="typography-body-4-medium">
            Daily reminder {formatDailyTime(settings.daily_time)}
            {zoneLabel ? ` ${zoneLabel}` : ""}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="flex w-72 flex-col gap-3">
        <div className="flex flex-col gap-1">
          <span className="typography-body-4-semibold">Daily reminder</span>
          <span className="text-black/60 typography-body-5">
            We remind you at this time, or an hour after your last dial block of
            the day if that's later.
          </span>
        </div>
        <DailyTimeSelect
          value={settings.daily_time}
          onChange={onChange}
          disabled={save.isPending}
          timeZoneLabel={getTimeZoneLabel(getBrowserTimeZone())}
          options={getDailyTimeOptions(getBrowserTimeZone())}
        />
      </PopoverContent>
    </Popover>
  );
};
